import { APIClient } from './api_client';

export interface PlaceLookupResponse {
  place: any; // Same format as sync_nearby_places.py creates
  found: boolean;
  placeId?: string;
  error?: string;
  match_type?: string; // 'place_id' or 'name_location'
  original_place_id?: string; // For name_location matches
}

export interface PlaceLookupResult {
  requestedPlaceId: string;
  found: boolean;
  data: any; // Same format as sync_nearby_places.py creates
  matchType?: string; // 'place_id' or 'name_location'
}

export interface BatchPlaceLookupResponse {
  results: PlaceLookupResult[];
  totalRequested: number;
  totalFound: number;
}

export class PlaceLookupClient {
  /**
   * Look up a single place in the existing Firebase collection
   */
  static async lookupPlace(placeId: string, name?: string, lat?: number, lng?: number): Promise<PlaceLookupResponse> {
    const requestData: any = { place_id: placeId };
    if (name && lat !== undefined && lng !== undefined) {
      requestData.name = name;
      requestData.lat = lat;
      requestData.lng = lng;
    }
    
    const response = await APIClient.post('/places/lookup/', requestData);
    return response.data;
  }

  /**
   * Look up multiple places in batch
   */
  static async lookupPlaces(placeIds: string[], placesData?: any[]): Promise<BatchPlaceLookupResponse> {
    const requestData: any = { place_ids: placeIds };
    if (placesData) {
      requestData.places_data = placesData;
    }
    
    const response = await APIClient.post('/places/lookup/', requestData);
    return response.data;
  }

  /**
   * Enrich AI-suggested places with data from the existing database
   */
  static async enrichAIPlaces(aiPlaces: any[]): Promise<any[]> {
    console.log('🔍 [PlaceLookupClient] Starting enrichment for AI places:', aiPlaces.map(p => ({ name: p.name, placeId: p.placeId })));
    
    // Extract place IDs from AI places
    const placeIds = aiPlaces
      .map(place => place.placeId)
      .filter(id => id); // Remove any null/undefined IDs

    if (placeIds.length === 0) {
      console.log('❌ [PlaceLookupClient] No valid place IDs found, returning original places');
      return aiPlaces;
    }

    try {
      // Prepare fallback data for name+location matching
      const placesData = aiPlaces
        .filter(place => place.placeId)
        .map(place => ({
          place_id: place.placeId,
          name: place.name,
          lat: place.lat,
          lng: place.lng,
        }));

      console.log('📤 [PlaceLookupClient] Sending lookup request for place IDs:', placeIds);

      // Look up places in the database with fallback data
      const lookupResponse = await this.lookupPlaces(placeIds, placesData);
      
      console.log('📥 [PlaceLookupClient] Received lookup response:', {
        totalRequested: lookupResponse.totalRequested,
        totalFound: lookupResponse.totalFound,
        results: lookupResponse.results?.map(result => ({
          requestedPlaceId: result.requestedPlaceId,
          found: result.found,
          hasResyId: !!(result.data?.resyId),
          resyId: result.data?.resyId,
          matchType: result.matchType
        }))
      });
      
      // Convert the results array to a Map for efficient lookup
      const lookupMap = new Map();
      if (lookupResponse.results) {
        lookupResponse.results.forEach((result: PlaceLookupResult) => {
          lookupMap.set(result.requestedPlaceId, result);
        });
      }

      // Enrich the original AI places with the data from the lookup map
      const enrichedPlaces = aiPlaces.map(originalPlace => {
        const lookupResult = lookupMap.get(originalPlace.placeId);
        
        if (lookupResult && lookupResult.found && lookupResult.data) {
          console.log(`✅ [PlaceLookupClient] Enriching "${originalPlace.name}" with database data:`, {
            originalPlaceId: originalPlace.placeId,
            databaseResyId: lookupResult.data.resyId,
            matchType: lookupResult.matchType,
            databasePlaceId: lookupResult.data.placeId,
          });

          // Merge original AI data with enriched data from database
          const enrichedPlace = {
            ...originalPlace,
            ...lookupResult.data,
            // CRITICAL FIX: Use database Place ID instead of preserving AI Place ID
            placeId: lookupResult.data.placeId, // Use database Place ID that contains resy_id
            type: 'ai_suggestion',
            // Preserve AI-generated metadata
            description: originalPlace.description,
            notes: originalPlace.notes,
            startTime: originalPlace.startTime,
            endTime: originalPlace.endTime,
            duration: originalPlace.duration,
            sequence: originalPlace.sequence,
            // Indicate this place has been enriched
            enriched: true,
            // Database places might have Resy IDs - EXPLICIT FIELD MAPPING
            resyId: lookupResult.data.resyId, // Convert resy_id to resyId
            bookable: !!(lookupResult.data.resyId),
            // Include match type for debugging
            matchType: lookupResult.matchType,
            // Store original AI Place ID for reference
            originalAIPlaceId: originalPlace.placeId
          };

          console.log(`✅ [PlaceLookupClient] Final enriched place "${originalPlace.name}":`, {
            name: enrichedPlace.name,
            placeId: enrichedPlace.placeId,
            resyId: enrichedPlace.resyId,
            resy_id: enrichedPlace.resy_id,
            enriched: enrichedPlace.enriched,
            bookable: enrichedPlace.bookable
          });

          return enrichedPlace;
        } else {
          console.log(`❌ [PlaceLookupClient] No enrichment data found for "${originalPlace.name}" (${originalPlace.placeId})`);
          // Return original place if not found in database
          return {
            ...originalPlace,
            type: 'ai_suggestion',
            enriched: false,
            bookable: false,
          };
        }
      });

      console.log('🎉 [PlaceLookupClient] Enrichment complete. Summary:', {
        totalPlaces: enrichedPlaces.length,
        enrichedCount: enrichedPlaces.filter(p => p.enriched).length,
        withResyId: enrichedPlaces.filter(p => p.resyId).length,
        resyIds: enrichedPlaces.map(p => ({ name: p.name, resyId: p.resyId })).filter(p => p.resyId)
      });

      return enrichedPlaces;
    } catch (error) {
      console.error('❌ [PlaceLookupClient] Error enriching AI places:', error);
      // Return original places if enrichment fails
      return aiPlaces.map(place => ({
        ...place,
        type: 'ai_suggestion',
        enriched: false,
        bookable: false,
      }));
    }
  }
}